// src/components/Pricing.jsx – Terminal‑themed
import { Link } from "react-router-dom";
import Navbar from "./navbar";
import { useAuth } from "../context/AuthContext";

/* ── brand accent + palette ───────────────────────── */
const ACCENT_BG     = "bg-green-500";
const ACCENT_TEXT   = "text-green-400";
const ACCENT_BORDER = "border-green-400";
const PANEL_BG      = "bg-[#161b22]";
const BORDER_CLR    = "border-[#30363d]";

/* ── Course constants ─────────────────────────────── */
const COURSE_PASS_ID   = process.env.REACT_APP_DEFAULT_COURSE_ID; // Mongo _id
const COURSE_PASS_CODE = "COURSE_101";

/* ── PLAN DEFINITIONS (numeric amounts) ───────────── */
const BASE_PLANS = {
  pass: {
    id: COURSE_PASS_ID,
    code: COURSE_PASS_CODE,
    type: "onetime",
    title: "Full‑Course Pass",
    priceInr: 499,
    priceUsd: 14,
    subtitle: "One‑time • lifetime access to 120‑day curriculum",
    features: [
      "Complete 120‑day interactive course",
      "Daily exercises for hands‑on learning",
      "Doubt‑clearing support from tech pros",
    ],
  },
  membership: {
    id: "randomcodetobecreated",
    code: "yearly_sub",
    type: "subscription",
    title: "Continuing‑Ed Membership",
    priceInr: 99,
    priceUsd: 2,
    subtitle: "Billed yearly • advanced micro‑courses & live clinics",
    features: [
      "Monthly live AMAs & code reviews",
      "New micro‑courses each quarter",
      "Full forum + mentor Q&A",
    ],
    comingSoon: true,
  },
};

/* ─────────────────────────────────────────────────── */
export default function Pricing() {
  const { user } = useAuth();
  const currency = getCurrency();

  /* do we own the main course already? */
  const ownsCourse = (user?.purchasedCourses || []).some(
    (id) => id.toString() === COURSE_PASS_ID
  );

  /* build formatted display strings */
  const PLANS = {
    pass: {
      ...BASE_PLANS.pass,
      price:
        currency === "INR"
          ? `₹${BASE_PLANS.pass.priceInr.toLocaleString("en-IN")}`
          : `$${BASE_PLANS.pass.priceUsd.toLocaleString("en-US")}`,
    },
    membership: {
      ...BASE_PLANS.membership,
      price:
        currency === "INR"
          ? `₹${BASE_PLANS.membership.priceInr.toLocaleString("en-IN")} / mo`
          : `$${BASE_PLANS.membership.priceUsd.toLocaleString("en-US")} / mo`,
    },
  };

  return (
    <div className="min-h-screen flex flex-col font-mono bg-[#0d1117] text-[#c9d1d9]">
      <Navbar />

      {/* ── Pricing grid ───────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-16">
          <span className="before:content-['$'] before:mr-2 before:text-green-500">
            Choose your plan
          </span>
        </h1>

        <div className="grid gap-12 lg:grid-cols-2">
          {/* Pass */}
          <PlanCard
            data={PLANS.pass}
            cta={
              ownsCourse
                ? { label: "Go to Course", to: "/lessons", state: null, disabled: false }
                : {
                    label: "Buy Now",
                    to: "/checkout",
                    state: {
                      planId: COURSE_PASS_ID,
                      planType: "onetime",
                      label: PLANS.pass.title,
                      currency,
                    },
                    disabled: false,
                  }
            }
          />

          {/* Membership */}
          <PlanCard
            data={PLANS.membership}
            comingSoon={PLANS.membership.comingSoon}
            cta={{ label: "Coming Soon", to: null, disabled: true }}
          />
        </div>
      </section>
    </div>
  );
}

/* ═══════════════════ PlanCard ════════════════════ */
function PlanCard({ data, cta, comingSoon = false }) {
  const btnEnabled = `${ACCENT_BG} hover:bg-green-600 text-black`;
  const btnDisabled = "bg-gray-600 text-gray-400 cursor-not-allowed";
  const buttonStyle = cta.disabled ? btnDisabled : btnEnabled;

  return (
    <div className="relative">
      {comingSoon && (
        <span className="absolute -top-3 -right-3 ${PANEL_BG} ${BORDER_CLR} text-xs px-2 py-1 rounded ${ACCENT_TEXT}">
          Coming&nbsp;Soon
        </span>
      )}

      <div className={`${PANEL_BG} ${BORDER_CLR} border rounded-3xl p-10 flex flex-col h-full shadow-lg`}>
        <h3 className="text-2xl font-bold mb-1">{data.title}</h3>
        <p className="text-[#8b949e] mb-6">{data.subtitle}</p>

        <div className={`text-4xl font-extrabold mb-8 ${ACCENT_TEXT}`}>{data.price}</div>

        <ul className="space-y-2 mb-10">
          {data.features.map((f) => (
            <li key={f} className="flex items-start text-sm">
              <span className={`mr-2 mt-0.5 ${ACCENT_TEXT}`}>✔︎</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {cta.to ? (
          <Link
            to={cta.to}
            state={cta.state}
            className={`mt-auto text-center px-6 py-4 rounded-lg font-semibold transition ${buttonStyle}`}
          >
            {cta.label}
          </Link>
        ) : (
          <span className={`mt-auto text-center px-6 py-4 rounded-lg ${buttonStyle} select-none`}>
            {cta.label}
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────── */
function getCurrency() {
  try {
    const opts = Intl.DateTimeFormat().resolvedOptions();
    if (opts.timeZone && /Asia\/(Kolkata|Calcutta)/i.test(opts.timeZone)) return "INR";
    const cc = (opts.locale || "").split("-")[1] || "";
    return cc.toUpperCase() === "IN" ? "INR" : "USD";
  } catch {
    return "USD";
  }
}
